// pages/TravelExpense/TravelWorkspaceTabs.js
//
// Go-live parity (2026-09-24): audit 09-travel #2/#3/#4 — the post-approval
// part of the legacy trip workspace (frmTravelExpense.vb), shown on the trip
// detail page once the trip is approved: Expenses, Trip Report and
// Pre-Arrangements. What each viewer may do comes from
// GET TravelExpense/{id}/workspace (server-side rules).
// Deep links: ?tab=expenses|report|arrangements and &emp=<empId>.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody, Alert } from "reactstrap";
import { useSearchParams } from "react-router-dom";
import { ResponsiveTabs } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { toCamel } from "./travelCase";
import TravelExpensesPanel from "./TravelExpensesPanel";
import TravelTripReportPanel from "./TravelTripReportPanel";
import TravelArrangementPanel from "./TravelArrangementPanel";

const TravelWorkspaceTabs = ({ tripId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [ws, setWs] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        api.get(`TravelExpense/${tripId}/workspace`)
            .then(({ data }) => { setWs(toCamel(data)); setError(null); })
            .catch((e) => setError(e?.response?.data?.error || e?.message || "Failed to load the trip workspace"));
    }, [tripId]);
    useEffect(load, [load]);

    const tabs = useMemo(() => [
        { key: "expenses", label: "Expenses", icon: "ri-money-dollar-box-line" },
        { key: "report", label: "Trip Report", icon: "ri-file-text-line" },
        { key: "arrangements", label: "Pre-Arrangements", icon: "ri-hotel-line" },
    ], []);

    const wanted = searchParams.get("tab");
    const active = tabs.some((t) => t.key === wanted) ? wanted : "expenses";
    const setActive = (k) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", k);
        setSearchParams(next);   // Owner 2026-10-12: Back returns to the previous tab
    };

    if (error) return <Alert color="danger" className="mt-3">{error}</Alert>;
    // Legacy: the workspace tabs exist only once the trip is approved (3/5/7).
    if (!ws || !ws.postApproval) return null;

    return (
        <Card className="mt-3">
            <CardBody>
                <h5 className="mb-3">Trip workspace</h5>
                <ResponsiveTabs tabs={tabs} activeKey={active} onChange={setActive} />
                {active === "expenses" && (
                    <TravelExpensesPanel tripId={tripId} workspace={ws} initialEmpId={searchParams.get("emp")} onChanged={load} />
                )}
                {active === "report" && <TravelTripReportPanel tripId={tripId} />}
                {active === "arrangements" && (
                    <TravelArrangementPanel tripId={tripId} travelerCount={(ws.travelers || []).length} onChanged={load} />
                )}
            </CardBody>
        </Card>
    );
};

export default TravelWorkspaceTabs;
